/** Matches `--exploration-canvas-bg` in exploration.css — dim blends target this surface. */
import { volumeBubbleStyleTokens } from '../../lib/bubble/volumeBubbleStyles';

export const explorationCanvasBg = '#fafbfc';

/** Static token values from index.css — used when getComputedStyle is unavailable. */
export const explorationDesignTokens: Record<string, string> = {
  '--exploration-canvas-bg': explorationCanvasBg,
  '--color-white': '#ffffff',
  '--color-gray-100': '#f7f7f7',
  '--color-gray-200': '#f1f2f4',
  '--color-gray-300': '#dbdde4',
  '--color-gray-400': '#ccced3',
  '--color-primary-200': '#a6b9fb',
  '--color-primary-100': '#d3dcfd',
  '--color-primary-500': '#2151f5',
  '--color-primary-70': '#dce5f3',
  '--color-magnolia': '#f3e0ff',
  '--color-pale-yellow': '#fff1bd',
  '--color-light-green': '#caf6de',
  '--activation-active': '#69dea7',
  '--activation-active-fill': '#e9f9f0',
  '--activation-toBeActivated': '#459eff',
  '--activation-toBeActivated-fill': '#eff7ff',
  '--activation-potentiallySupported': '#ffa100',
  '--activation-potentiallySupported-fill': '#fff4e8',
  '--activation-unsupported': '#dcdfe7',
  '--activation-unsupported-fill': '#f3f4f7',
  '--exploration-supported': '#5caaff',
  '--color-pale-pink': '#fedce4',
  '--color-primary-50': '#edf1fe',
  '--color-primary-900': '#071031',
  '--color-gray-700': '#5c6380',
  '--color-gray-800': '#41475e',
};

/** Infrastructure bubble map — single source of truth for outline, chip, filter, and emphasis tokens. */
export const infraBubbleStyles = {
  bubble: {
    fill: '#ffffff',
    border: {
      default: explorationDesignTokens['--color-primary-100'] ?? '#d3dcfd',
      hover: explorationDesignTokens['--color-primary-500'] ?? '#2151f5',
      /** Dimmed with no highlighted descendants. */
      dim: explorationDesignTokens['--color-gray-100'] ?? '#f7f7f7',
      /** Dimmed ancestor that still contains a bright/highlighted descendant. */
      dimWithHighlightedChildren:
        explorationDesignTokens['--color-primary-100'] ?? '#d3dcfd',
    },
    shadowFilter: {
      default: 'url(#b-infra-default-shadow)',
      hover: 'url(#b-infra-hover-shadow)',
      overflowHover: 'url(#b-overflow-hover-shadow)',
    },
  },
  chip: {
    bg: 'var(--color-white)',
    accountNameBg: 'var(--color-gray-300)',
    /** Account name chip fill while dimmed — keeps gray-300 text readable. */
    accountNameBgDim: '#FCFCFC',
    border: '#EAEFF5',
    text: {
      /** Name chip / managed volume label — resolved hex for reliable SVG fill. */
      name: explorationDesignTokens['--color-primary-900'] ?? '#071031',
      /** Unmanaged volume name line. */
      nameUnmanaged: explorationDesignTokens['--color-gray-800'] ?? '#41475e',
      size: explorationDesignTokens['--color-gray-700'] ?? '#5c6380',
      dim: explorationDesignTokens['--color-gray-300'] ?? '#dbdde4',
    },
    layout: {
      fontSize: 12,
      fontWeight: 500,
      radius: 4,
      padX: 3,
      padY: 3,
      iconGap: 4,
      minTextChars: 6,
      name: { minR: 18, maxWidth: 150 },
      size: { minR: 18, maxWidth: 130 },
    },
    sizeShadowFilter: 'url(#b-chip-shadow-size)',
  },
  icon: {
    dimOpacity: '0.3',
    badgeSize: 16,
    badgeRadius: 4.571,
    glyphSize: 12,
  },
  emphasis: {
    dimmedFill: explorationDesignTokens['--color-white'] ?? '#ffffff',
    dimmedStroke: explorationDesignTokens['--color-gray-100'] ?? '#f7f7f7',
    elementOpacity: 0.1,
    highlightStroke: 'var(--color-primary-500)',
    highlightStrokeResolved: explorationDesignTokens['--color-primary-500'] ?? '#2151f5',
  },
  svgFilterDefs: `
  <filter id="b-chip-shadow-size" x="-40%" y="-150%" width="180%" height="400%">
    <feDropShadow dx="0" dy="1" stdDeviation="3.625" flood-color="#000000" flood-opacity="0.15"/>
  </filter>
  <filter id="b-infra-default-shadow" x="-50%" y="-50%" width="200%" height="200%">
    <feDropShadow dx="0" dy="0" stdDeviation="9.45" flood-color="#000000" flood-opacity="0.02"/>
  </filter>
  <filter id="b-infra-hover-shadow" x="-50%" y="-50%" width="200%" height="200%">
    <feDropShadow dx="0" dy="0" stdDeviation="6" flood-color="#DADDE7" flood-opacity="1"/>
  </filter>
  <filter id="b-overflow-hover-shadow" x="-50%" y="-50%" width="200%" height="200%">
    <feDropShadow dx="0" dy="0" stdDeviation="6" flood-color="#E6E6E6" flood-opacity="1"/>
  </filter>
`,
} as const;

export type InfraBubbleStyles = typeof infraBubbleStyles;

/** Volume / status / overflow bubble map — non-infrastructure canvas styling. */
export const volumeBubbleStyles = {
  ...volumeBubbleStyleTokens,
  volume: {
    ...volumeBubbleStyleTokens.volume,
    hover: {
      ...volumeBubbleStyleTokens.volume.hover,
      shadowFilter: infraBubbleStyles.bubble.shadowFilter.hover,
    },
    emphasis: {
      shadowFilter: infraBubbleStyles.bubble.shadowFilter.hover,
    },
  },
  overflow: {
    ...volumeBubbleStyleTokens.overflow,
    hoverShadowFilter: infraBubbleStyles.bubble.shadowFilter.overflowHover,
  },
  emphasis: infraBubbleStyles.emphasis,
} as const;

export { volumeBubbleStyleTokens } from '../../lib/bubble/volumeBubbleStyles';
export type { VolumeBubbleStyleTokens } from '../../lib/bubble/volumeBubbleStyles';

export type VolumeBubbleStyles = typeof volumeBubbleStyles;

export function parseColorToRgb(color: string): [number, number, number] | null {
  const trimmed = color.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
      ];
    }
    if (hex.length === 6) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
    return null;
  }

  const rgbMatch = trimmed.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
  if (rgbMatch) {
    return [
      Math.round(Number(rgbMatch[1])),
      Math.round(Number(rgbMatch[2])),
      Math.round(Number(rgbMatch[3])),
    ];
  }

  return null;
}

export function resolveColorString(color: string): string | null {
  const trimmed = color.trim();
  if (!trimmed || trimmed === 'none') return null;
  if (parseColorToRgb(trimmed)) return trimmed;

  const varMatch = trimmed.match(/^var\(\s*(--[\w-]+)\s*(?:,\s*(.+))?\s*\)$/);
  if (varMatch) {
    const token = varMatch[1];
    const fromMap = explorationDesignTokens[token];
    if (fromMap) return fromMap;
    const fallback = varMatch[2]?.trim();
    if (fallback) return resolveColorString(fallback);
  }

  return null;
}

export function blendRgb(
  fg: [number, number, number],
  bg: [number, number, number],
  alpha: number
): string {
  const r = Math.round(fg[0] * alpha + bg[0] * (1 - alpha));
  const g = Math.round(fg[1] * alpha + bg[1] * (1 - alpha));
  const b = Math.round(fg[2] * alpha + bg[2] * (1 - alpha));
  return `rgb(${r}, ${g}, ${b})`;
}

export function blendColorWithBackground(
  color: string,
  alpha: number,
  bg = explorationCanvasBg
): string | null {
  const resolved = resolveColorString(color) ?? color;
  const fgRgb = parseColorToRgb(resolved);
  const bgRgb = parseColorToRgb(bg);
  if (!fgRgb || !bgRgb) return null;
  return blendRgb(fgRgb, bgRgb, alpha);
}
